// Glue between the sell screen slice and the pure discount engine.
// The slice stores engine inputs only; repriceCart reruns the engine from scratch and
// writes its output back into the fields the existing screens, receipt and payment read.
import { initialStateType } from "../types/sellScreenTypes";
import {
	calculateCart,
	CalculateCartInput,
	CouponMode,
	discountsFromStore,
	normaliseCartLine,
	normaliseCoupon,
	OrderAdjustment,
	toMajor,
	toMinor,
} from "./discountEngine";

export const DISCOUNT_CONFIG: { couponBlockedByLineDiscount: CouponMode } = {
	// 'line': a coupon skips lines that already have a line discount
	// 'order': any line discount blocks the coupon for the whole order
	couponBlockedByLineDiscount: "line",
};

// Return / replacement carts carry server-computed discounts and tax from the original order,
// so they keep the legacy calculation. Only sales go through the engine.
export const usesDiscountEngine = (state: initialStateType, locationId: number) =>
	state.sellScreenType === "SELL" && !(state.cartProducts?.[locationId] ?? []).some((item) => item.quantity < 0);

const toAdjustment = (type: string | undefined, value: number | undefined): OrderAdjustment => {
	if (!type) return null;
	const isFixed = String(type).toUpperCase() === "FIXED";
	return { type: isFixed ? "FIXED" : "PERCENTAGE", value: isFixed ? toMinor(value) : Number(value) || 0 };
};

export const buildEngineInput = (state: initialStateType, locationId: number, now: number = Date.now()): CalculateCartInput => ({
	lines: (state.cartProducts?.[locationId] ?? []).map(normaliseCartLine),
	customerId: state.customerDetails?.customerId ?? null,
	discounts: discountsFromStore(state.discountStore?.[locationId]),
	claimedOffers: state.claimedOffers?.[locationId] ?? [],
	automaticChoice: state.automaticChoice?.[locationId] ?? {},
	coupon: normaliseCoupon(state.manualDiscount?.[locationId]),
	additionalDiscount: toAdjustment(state.additionalDiscount?.[locationId]?.type, state.additionalDiscount?.[locationId]?.discount),
	additionalCharge: toAdjustment(state.additionalCharge?.[locationId]?.type, state.additionalCharge?.[locationId]?.charge),
	tip: toMinor(state.tip?.[locationId] ?? 0),
	now,
	config: { ...DISCOUNT_CONFIG, roundOffSale: Boolean(state.roundOffSale) },
	previousAppliedDiscountIds: state.pricedCart?.[locationId]?.appliedDiscountIds,
});

export const repriceCart = (state: initialStateType, locationId: number) => {
	if (!state.pricedCart) state.pricedCart = {};
	if (!state.claimedOffers) state.claimedOffers = {};
	if (!state.automaticChoice) state.automaticChoice = {};
	const priced = calculateCart(buildEngineInput(state, locationId));
	state.pricedCart[locationId] = priced;

	// Claims the engine dropped are removed; its message explains why
	state.claimedOffers[locationId] = (state.claimedOffers[locationId] ?? []).filter((claim) =>
		priced.claims.find((result) => result.discountId === claim.discountId && result.getVariantId === claim.getVariantId)?.status !== "DROPPED"
	);

	state.cartProducts[locationId] = (state.cartProducts[locationId] ?? []).map((item) => {
		const line = priced.lines.find((pricedLine) => pricedLine.key === String(item.variantId));
		if (!line) return item;
		const lineDiscount = line.discount?.amount ?? 0;
		return {
			...item,
			autoDiscount: toMajor(lineDiscount),
			discount: toMajor(lineDiscount + line.couponShare + line.additionalShare),
			finalPrice: toMajor(line.taxable),
			tax: toMajor(line.tax),
		};
	});

	const { totals, coupon } = priced;
	state.cartTotal[locationId] = toMajor(totals.subtotal);
	state.cartQuantity[locationId] = totals.quantity;
	state.automaticDiscount[locationId] = DISCOUNT_CONFIG.couponBlockedByLineDiscount === "order" && totals.lineDiscount > 0;
	const manual = state.manualDiscount?.[locationId];
	if (manual) {
		state.manualDiscount[locationId] = { ...manual, isValidate: Boolean(coupon?.applied), discountAmount: toMajor(coupon?.amount ?? 0) };
	}
	const additional = state.additionalDiscount?.[locationId];
	if (additional) {
		state.additionalDiscount[locationId] = { ...additional, discountAmount: toMajor(totals.additionalDiscount) };
	}
	const charge = state.additionalCharge?.[locationId];
	if (charge) {
		state.additionalCharge[locationId] = { ...charge, amount: toMajor(totals.additionalCharge) };
	}
	state.summary[locationId] = {
		subTotal: toMajor(totals.subtotal),
		otherDiscount: toMajor(totals.lineDiscount),
		couponDiscount: toMajor(totals.couponDiscount),
		additionalDiscount: toMajor(totals.additionalDiscount),
		discountedPrice: toMajor(totals.discountedSubtotal),
		tax: toMajor(totals.tax),
		grandTotal: toMajor(totals.grandTotal),
		roundOff: toMajor(totals.roundOff),
		payableTotal: toMajor(totals.payableTotal),
	};
};
